/**
 * Pure presentation rules for the Activity thread card.
 *
 * The card is three lines of dense metadata, and almost every decision in it is
 * a mapping from thread state to a label, a tone, and an emphasis. Keeping
 * those mappings here means they can be tested as data — the component is then
 * only layout, which is the part a unit test cannot usefully assert anyway.
 *
 * The tones and weights below are t3code's, not Forge's project-row palette:
 * every hue is its `-600/-700` light and `-300/-400` dark pair, so a card reads
 * exactly as it does there. Forge's status vocabulary is richer (Conflicted,
 * Stuck, Connecting, Needs Review, Plan Ready, Interrupted have no t3code
 * equivalent), so those extra labels are placed into the nearest t3code hue
 * while keeping the same weight convention.
 */

import { cn } from "~/lib/utils";
import { resolveSidebarRowSurfaceTone } from "./sidebarRowSurface";

import type { ProviderKind } from "@cmmd-forge/contracts";
import type { ThreadStatusPill } from "../Sidebar.logic";
import type { ThreadPr } from "./SidebarThreadRow.helpers";

type StatusLabel = ThreadStatusPill["label"];

/**
 * The right end of the card's first line.
 *
 * The glyph marks the two states worth recognising without reading: work in
 * progress, and work that finished while you were away.
 */
export interface ActivityCardStatusModel {
  readonly label: string;
  readonly colorClass: string;
  readonly glyph: "working" | "done" | null;
}

/** t3code's hue per status, at its `-600/-700` ↔ `-300/-400` weights. */
const STATUS_TONE: Record<StatusLabel, string> = {
  // sky — in flight (t3code: Working, Monitoring)
  Working: "text-hue-sky-foreground",
  Connecting: "text-hue-sky-foreground",
  // emerald — finished (t3code: Done)
  Completed: "text-success-foreground",
  // amber — blocked on you (t3code: Approval, Woke)
  "Pending Approval": "text-warning-foreground",
  Interrupted: "text-warning-foreground",
  // indigo — blocked on your words (t3code: Input)
  "Awaiting Input": "text-hue-indigo-foreground",
  // red — broken (t3code: Failed)
  Failed: "text-destructive-foreground",
  Conflicted: "text-destructive-foreground",
  // No t3code equivalent; same weight convention, kept distinct from Approval
  // so Forge's extra states do not collapse into one another.
  Stuck: "text-hue-orange-foreground",
  "Needs Review": "text-hue-violet-foreground",
  "Plan Ready": "text-hue-violet-foreground",
};

const WORKING_STATUS_LABELS = new Set<StatusLabel>(["Working", "Connecting"]);

export function resolveActivityCardStatus(input: {
  readonly threadStatus: ThreadStatusPill | null;
  /** The thread the user is currently looking at. */
  readonly isActive: boolean;
}): ActivityCardStatusModel | null {
  const { threadStatus } = input;
  if (threadStatus === null) return null;
  const isWorking = WORKING_STATUS_LABELS.has(threadStatus.label);
  const glyph = isWorking
    ? ("working" as const)
    : threadStatus.label === "Completed"
      ? ("done" as const)
      : null;
  return {
    label: threadStatus.label,
    colorClass: cn(
      STATUS_TONE[threadStatus.label] ?? threadStatus.colorClass,
      // t3code's rule, and the reason a sidebar full of working threads stays
      // quiet: "Working" is a background state, so it rests at the dim end of
      // what the old shimmer used to cycle through. Only the thread you have
      // open gets the label at full strength. It is deliberately the one status
      // that dims — the others are all either finished or waiting on a human.
      isWorking && !input.isActive ? "opacity-75" : null,
    ),
    glyph,
  };
}

/**
 * How loudly a card speaks.
 *
 * t3code's rule, and the reason its sidebar stays calm: prominence is reserved
 * for rows that need a human. Work that is merely in flight is not your problem
 * yet, so it recedes exactly as far as an already-read row does — only the
 * coloured status label stands out.
 */
export interface ActivityCardEmphasisModel {
  /** Nothing is waiting on you here — dim the row. */
  readonly shouldRecede: boolean;
  /** Running, or blocked on an approval/input you have not given yet. */
  readonly isInFlight: boolean;
  /** Finished while you were away, and you have not looked yet. */
  readonly isUnread: boolean;
}

const IN_FLIGHT_STATUS_LABELS = new Set<StatusLabel>([
  "Working",
  "Connecting",
  "Pending Approval",
  "Awaiting Input",
]);

/**
 * The surface every Activity row draws on — card and archived row alike.
 *
 * t3code's model, and the reason one function serves both arrangements: status
 * lives in a row's *content*, and the surface is reserved for interaction —
 * hover, multi-select, route. Its sidebar used to make live threads elevated
 * cards and settled threads plain rows, which produced neither a useful
 * hierarchy nor a reliable hover cue. Two components differing only in how they
 * lay their content out must not also differ in what a hover looks like.
 *
 * In-flight rows fade as a whole, because there is nothing for a human to do on
 * them yet.
 */
export function resolveActivityRowSurfaceClassName(input: {
  readonly isActive: boolean;
  readonly isSelected: boolean;
  readonly emphasis: ActivityCardEmphasisModel;
}): string {
  return cn(
    "relative w-full cursor-pointer overflow-hidden rounded-md text-left outline-none select-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
    resolveSidebarRowSurfaceTone({
      isActive: input.isActive,
      isSelected: input.isSelected,
      shouldRecede: input.emphasis.shouldRecede,
    }),
    input.emphasis.isInFlight &&
      !input.isActive &&
      !input.isSelected &&
      "opacity-70 transition-opacity motion-reduce:transition-none hover:opacity-100",
  );
}

export function resolveActivityCardEmphasis(input: {
  readonly threadStatus: ThreadStatusPill | null;
  readonly isActive: boolean;
  readonly isSelected: boolean;
}): ActivityCardEmphasisModel {
  const label = input.threadStatus?.label ?? null;
  const isInFlight = label !== null && IN_FLIGHT_STATUS_LABELS.has(label);
  const isUnread = label === "Completed";
  const isQuiet = label === null || isInFlight;
  return {
    isInFlight,
    isUnread,
    shouldRecede: isQuiet && !isUnread && !input.isActive && !input.isSelected,
  };
}

/**
 * The `#123` badge on the third line.
 *
 * Distinct from `prStatusIndicator`, which answers a narrower question — "is
 * there a merged PR worth promoting to the row's lead indicator?" and so
 * returns null for open and closed PRs. The card has room to show the PR
 * whatever state it is in, which is the more useful thing when you are
 * scanning for what still needs landing.
 */
export interface ActivityCardPrBadgeModel {
  readonly label: string;
  readonly colorClass: string;
  readonly tooltip: string;
  readonly url: string;
}

/**
 * t3code's `prStatusIndicator` hues, verbatim.
 *
 * These are the `-600 / -300/90` pairs it uses for every change-request state,
 * including closed — which reads red there rather than as muted chrome. A
 * closed PR is a fact about the work, not absent metadata, so it keeps a hue.
 */
const PR_STATE_COLOR_CLASS: Record<"open" | "closed" | "merged", string> = {
  open: "text-success-foreground",
  merged: "text-hue-violet-foreground",
  closed: "text-destructive-foreground",
};

/**
 * "PR" is t3code's GitHub short name (`GITHUB_CHANGE_REQUEST_PRESENTATION`).
 * It is a constant here rather than resolved from a provider because Forge
 * models GitHub only — t3code varies it to "MR" on GitLab, and hard-coding the
 * lookup would be inventing a provider abstraction Forge does not have.
 */
const CHANGE_REQUEST_SHORT_NAME = "PR";

function formatPrState(state: "open" | "closed" | "merged"): string {
  return state.charAt(0).toUpperCase() + state.slice(1);
}

export function resolveActivityCardPrBadge(pr: ThreadPr | null): ActivityCardPrBadgeModel | null {
  if (!pr) return null;
  return {
    label: `#${pr.number}`,
    colorClass: PR_STATE_COLOR_CLASS[pr.state],
    // t3code's `formatPrStatusLead`: "PR #123 - Merged: <title>".
    tooltip: `${CHANGE_REQUEST_SHORT_NAME} #${pr.number} - ${formatPrState(pr.state)}: ${pr.title}`,
    url: pr.url,
  };
}

/**
 * The archived row's PR badge: the state's colour is held back until hover.
 *
 * t3code's rule for its settled shelf — history recedes into one grey tone so
 * the live list above keeps every hue, and the colour returns the moment you
 * are actually reading the shelf. The badge is the one exception to the row's
 * own hover fade, because it stays clickable while hovered.
 *
 * The group is `menu-sub-item`, not t3code's `sidebar-row`: `SidebarMenuSubItem`
 * is the `<li>` every Forge sidebar row renders into and it owns that name.
 * Upstream's own helper still emits a `v2-row` group left over from a rename,
 * so the tint never fires there — copying the literal would have shipped a
 * class that reads correct and does nothing.
 *
 * Spelled out rather than derived from `PR_STATE_COLOR_CLASS` at runtime:
 * Tailwind generates CSS from the class strings it can *see* in the source, so
 * a name built by concatenation would resolve to no rule at all. The guard
 * against the two tables drifting is a test, not a mapping function.
 */
const PR_STATE_HOVER_COLOR_CLASS: Record<"open" | "closed" | "merged", string> = {
  open: "group-hover/menu-sub-item:text-success-foreground",
  merged: "group-hover/menu-sub-item:text-hue-violet-foreground",
  closed: "group-hover/menu-sub-item:text-destructive-foreground",
};

export function resolveArchivedRowPrHoverClass(pr: ThreadPr | null): string | null {
  return pr ? PR_STATE_HOVER_COLOR_CLASS[pr.state] : null;
}

/**
 * The branch line's fallback.
 *
 * A thread with no branch still needs the third line to hold its height —
 * otherwise cards in the same list would be two different sizes, which reads as
 * a rendering bug rather than as missing data. The worktree path is the next
 * best identifier; its basename is what distinguishes one worktree from another.
 */
export function resolveActivityCardBranchLabel(thread: {
  /**
   * The branch to show — `resolveDisplayedThreadBranch`'s answer, which prefers
   * the live checkout over the value frozen at provisioning. Passing
   * `thread.branch` straight in is what made every unopened row read as the
   * branch it was created from.
   */
  readonly branch: string | null;
  readonly worktreePath: string | null;
}): string | null {
  if (thread.branch !== null && thread.branch.trim() !== "") return thread.branch;
  if (thread.worktreePath === null) return null;
  const segments = thread.worktreePath.split(/[/\\]/).filter((segment) => segment !== "");
  return segments.at(-1) ?? null;
}

/**
 * Which timestamp the card ages from.
 *
 * Preferring the last user message over `updatedAt` keeps a thread from looking
 * fresh because a background turn touched it; the list is ordered by human
 * attention, so the label should agree with the ordering.
 */
export function resolveActivityCardTimestamp(thread: {
  readonly latestUserMessageAt: string | null;
  readonly updatedAt?: string | undefined;
  readonly createdAt: string;
}): string {
  return thread.latestUserMessageAt ?? thread.updatedAt ?? thread.createdAt;
}

/**
 * The resting label's shape, which is t3code's `compactSidebarTimeLabel`.
 *
 * Both apps produce the same relative string, and both then have to fit it into
 * a slot the hover actions take over — so upstream drops the suffix rather than
 * the number: "2h", not "2h ago", and "now" rather than "just now". Forge was
 * rendering the uncompacted form, which is a visibly wider label in exactly the
 * slot the card is tightest.
 *
 * Written as a string transform, not a second formatter, for the same reason it
 * is upstream: there is one relative-time implementation and this is a
 * presentation rule layered on it, so the two cannot drift apart.
 */
export function compactActivityCardTimeLabel(label: string): string {
  if (label === "just now") return "now";
  return label.endsWith(" ago") ? label.slice(0, -4) : label;
}

/**
 * When a working thread's elapsed counter starts from.
 *
 * The running turn is the truth: `startedAt` once the runtime has adopted the
 * turn, `requestedAt` for the window between the user sending and the runtime
 * picking it up — otherwise a thread reads "0s" for as long as it waits to be
 * adopted, which is precisely the interval worth seeing. The session's last
 * transition is the fallback for when the turn projection lags behind the
 * status, so the counter degrades to "roughly right" rather than disappearing.
 *
 * Unparseable timestamps fall through to the next candidate rather than being
 * accepted: `Date.parse` on a malformed string yields NaN, which would render
 * as a frozen "0s" that looks like a stalled agent.
 */
export function resolveActivityCardWorkingStartedAt(thread: {
  readonly latestTurn: {
    readonly startedAt: string | null;
    readonly requestedAt: string;
    readonly completedAt: string | null;
  } | null;
  readonly session: { readonly updatedAt: string } | null;
}): string | null {
  const candidates =
    thread.latestTurn !== null && thread.latestTurn.completedAt === null
      ? [
          thread.latestTurn.startedAt,
          thread.latestTurn.requestedAt,
          thread.session?.updatedAt ?? null,
        ]
      : [thread.session?.updatedAt ?? null];
  for (const candidate of candidates) {
    if (candidate !== null && !Number.isNaN(Date.parse(candidate))) return candidate;
  }
  return null;
}

/**
 * t3code's elapsed-duration ramp: seconds, then whole minutes, then `Nh Mm`.
 *
 * It deliberately stops counting seconds after the first minute. A per-second
 * digit that never settles is the sidebar equivalent of a spinner, and the
 * question the label answers past a minute is "how long has this been going",
 * not "how long exactly".
 */
export function formatActivityCardWorkingDuration(elapsedMs: number): string {
  const seconds = Number.isFinite(elapsedMs) ? Math.max(0, Math.floor(elapsedMs / 1000)) : 0;
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * The worktree glyph's accessible name, and the only text the glyph carries.
 *
 * `null` when the thread works directly in the project directory: there is no
 * worktree to name, and t3code renders nothing at all in that case rather than
 * a disabled or empty glyph.
 */
export function resolveActivityCardWorktreeTooltip(input: {
  readonly branch: string | null;
  readonly worktreePath: string | null;
  readonly displayPath: string;
}): string | null {
  if (input.worktreePath === null || input.worktreePath.trim() === "") return null;
  return input.branch !== null && input.branch !== ""
    ? `Worktree: ${input.displayPath} (${input.branch})`
    : `Worktree: ${input.displayPath}`;
}

/**
 * The mark in front of a card's branch name.
 *
 * A deliberate divergence from t3code, which renders this **only** for a
 * worktree and nothing otherwise. Upstream's reasoning is sound — an
 * always-present glyph that is sometimes greyed out spends the branch name's
 * horizontal space to say nothing — but the conclusion does not carry here,
 * because a *different* glyph is not a greyed-out one. It still answers the
 * question the branch name cannot, and it keeps the branch line aligned with
 * every other metadata line in the card, all of which lead with a mark.
 *
 * Worktree wins when a thread has both: it is the rarer and more consequential
 * fact, and the branch is already written out beside it.
 */
export type ActivityCardBranchGlyph = {
  readonly kind: "worktree" | "branch";
  readonly tooltip: string;
};

export function resolveActivityCardBranchGlyph(input: {
  readonly branch: string | null;
  readonly worktreePath: string | null;
  readonly displayPath: string;
}): ActivityCardBranchGlyph | null {
  const worktreeTooltip = resolveActivityCardWorktreeTooltip(input);
  if (worktreeTooltip !== null) return { kind: "worktree", tooltip: worktreeTooltip };
  // No branch means nothing to qualify — the row renders a spacer instead.
  if (input.branch === null || input.branch.trim() === "") return null;
  return { kind: "branch", tooltip: `Branch: ${input.branch}` };
}

/**
 * Which agent ran this thread, in the order t3code resolves it
 * (Sidebar.tsx:924): the live session first, then Forge's own live composer
 * selection, then the thread's durable selection.
 *
 * The last step is the one Forge was missing. The first two are ephemeral — a
 * session exists only while something is running, and the shell entry is local
 * environment state a linked device never populates — so an idle row could name
 * no agent even though the thread shell knew exactly which one ran.
 *
 * Returns null when nothing knows, which is the honest answer for a thread
 * projected from the CMMD runtime inventory: that payload carries a model slug
 * and no provider kind, and the glyph map is keyed by provider.
 */
export function resolveActivityCardProvider(input: {
  readonly sessionProvider: ProviderKind | null | undefined;
  readonly shellProvider: ProviderKind | null | undefined;
  readonly durableProvider: ProviderKind | null | undefined;
}): ProviderKind | null {
  return input.sessionProvider ?? input.shellProvider ?? input.durableProvider ?? null;
}
